import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { updateUserDobNickname, updateConsent, getConsent } from "./api";
import RenderTranslatedText from "../../components/RenderTranslatedText";
import { analytics } from "../../analytics";

function ConsentRow({ label, checked, onChange, required = false }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer">
      <input
        type="checkbox"
        className="mt-0.5 w-4 h-4 shrink-0"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm otaDark">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
    </label>
  );
}

export default function ShopCustomerOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const stateUser = location.state?.user;
  const redirectTarget = location.state?.from || "/shop/shops";

  const needsBasicDetails = !stateUser || !stateUser.dob || !stateUser.nickname;

  const [step, setStep] = useState(needsBasicDetails ? 1 : 2);
  const [nickname, setNickname] = useState(stateUser?.nickname || "");
  const [dob, setDob] = useState(
    stateUser?.dob ? String(stateUser.dob).slice(0, 10) : "",
  );
  const [consent, setConsent] = useState({
    eulaAccepted: false,
    dataProcessingAccepted: false,
    analyticsTrackingAccepted: false,
    pushNotificationsAccepted: false,
    marketingCommsAccepted: false,
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getConsent()
      .then((res) => {
        const d = res.data;
        if (!d) return;
        // Only pick the writable boolean fields — the GET response also
        // includes eulaAcceptedAt/dataProcessingAcceptedAt/updatedAt, which
        // PUT /auth/consent rejects as unknown fields if sent back.
        setConsent((prev) => ({
          ...prev,
          eulaAccepted: d.eulaAccepted,
          dataProcessingAccepted: d.dataProcessingAccepted,
          analyticsTrackingAccepted: d.analyticsTrackingAccepted,
          pushNotificationsAccepted: d.pushNotificationsAccepted,
          marketingCommsAccepted: d.marketingCommsAccepted,
        }));
      })
      .catch(() => {});
  }, []);

  const handleBasicDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!nickname.trim()) {
      setError("Please enter a nickname.");
      return;
    }
    if (!dob) {
      setError("Please enter your date of birth.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const cuid = localStorage.getItem("userId");
      await updateUserDobNickname({ cuid, nickname: nickname.trim(), dob });
      localStorage.setItem("shopUserNickname", nickname.trim());
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleConsentSubmit = async (e) => {
    e.preventDefault();
    if (!consent.eulaAccepted || !consent.dataProcessingAccepted) {
      setError("Please accept the required terms to continue.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await updateConsent(consent);
      analytics.setConsent(!!consent.analyticsTrackingAccepted);
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-10">
      <div className="flex items-center gap-2 justify-center mb-8">
        <span
          className={`w-2.5 h-2.5 rounded-full ${step === 1 ? "otaGradientButton" : "bg-gray-200"}`}
        />
        <span
          className={`w-2.5 h-2.5 rounded-full ${step === 2 ? "otaGradientButton" : "bg-gray-200"}`}
        />
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-center mb-4">
          {error}
        </div>
      )}

      {step === 1 ? (
        <form
          onSubmit={handleBasicDetailsSubmit}
          className="flex flex-col gap-5"
        >
          <div>
            <h1 className="text-xl font-semibold otaDark">
              <RenderTranslatedText i8nKey="shop_onboarding_title" />
            </h1>
            <p className="otaLight text-sm mt-1">
              Just a couple of details before you shop.
            </p>
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium otaDark">
              <RenderTranslatedText i8nKey="shop_onboarding_nickname_label" />
            </span>
            <input
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              placeholder="What should we call you?"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium otaDark">
              <RenderTranslatedText i8nKey="shop_onboarding_dob_label" />
            </span>
            <input
              type="date"
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="otaGradientButton text-white font-semibold rounded-lg py-3 mt-2 disabled:opacity-60"
          >
            <RenderTranslatedText i8nKey="shop_onboarding_continue" />
          </button>
        </form>
      ) : (
        <form onSubmit={handleConsentSubmit} className="flex flex-col gap-4">
          <div>
            <h1 className="text-xl font-semibold otaDark">
              <RenderTranslatedText i8nKey="shop_onboarding_consent_title" />
            </h1>
            <p className="otaLight text-sm mt-1">Your privacy, your choice.</p>
          </div>

          <ConsentRow
            label="Accept Terms of Use"
            required
            checked={consent.eulaAccepted}
            onChange={(v) => setConsent((c) => ({ ...c, eulaAccepted: v }))}
          />
          <ConsentRow
            label="Allow processing my data to place orders"
            required
            checked={consent.dataProcessingAccepted}
            onChange={(v) =>
              setConsent((c) => ({ ...c, dataProcessingAccepted: v }))
            }
          />
          <ConsentRow
            label="Help us improve with anonymous analytics"
            checked={consent.analyticsTrackingAccepted}
            onChange={(v) =>
              setConsent((c) => ({ ...c, analyticsTrackingAccepted: v }))
            }
          />
          <ConsentRow
            label="Send me order updates via push notification"
            checked={consent.pushNotificationsAccepted}
            onChange={(v) =>
              setConsent((c) => ({ ...c, pushNotificationsAccepted: v }))
            }
          />
          <ConsentRow
            label="Send me offers & promotions"
            checked={consent.marketingCommsAccepted}
            onChange={(v) =>
              setConsent((c) => ({ ...c, marketingCommsAccepted: v }))
            }
          />

          <button
            type="submit"
            disabled={saving}
            className="otaGradientButton text-white font-semibold rounded-lg py-3 mt-2 disabled:opacity-60"
          >
            <RenderTranslatedText i8nKey="shop_onboarding_continue" />
          </button>
        </form>
      )}
    </div>
  );
}
